/**
 * CÓMO SE ESCRIBE LA PLATA EN PANTALLA.
 *
 * Toda la app formateaba los importes con `{ style: 'currency', currency: 'MXN' }`
 * escrito a mano, en siete lugares distintos. Con un probador en Chile, catálogos
 * cargados en CLP y COP y la salida a varios países, eso significa que un
 * presupuesto chileno se veía en pesos mexicanos: el número correcto con la
 * moneda equivocada, que es peor que no poner nada.
 *
 * Las dos reglas:
 *
 *   1. La moneda que se muestra es LA DE LA OBRA — la misma que decidió qué
 *      catálogos se consultaron. Llega en `quote.moneda` / `project.moneda`.
 *   2. Si no se sabe (`null`), el importe va SIN símbolo. Un número sin moneda es
 *      mejor que un número con una moneda que miente.
 *
 * Sobre los separadores: la agrupación se queda en `es-MX` ("1,234.56") para toda
 * la app, y lo que cambia es la moneda. Es a propósito: en la misma tabla conviven
 * importes, cantidades, porcentajes y conteos, y todos se formatean con `es-MX`;
 * cambiar solo la columna de plata a la puntuación chilena ("1.234,56") dejaría dos
 * convenciones de decimal en la misma fila, que es la manera más rápida de leer un
 * número mal. `Intl` resuelve la parte importante: con `es-MX` y una moneda que no
 * es MXN escribe el CÓDIGO, no el signo — "CLP 1,234.56", no "$1,234.56" —, así que
 * el número nunca se puede confundir de país. Si dirección prefiere la puntuación
 * local, se cambia acá y en un solo lugar (`LOCALE`), no en siete.
 */

import { createContext, useContext } from 'react';

/** La app habla es-MX; los números también. Ver el comentario de arriba. */
const LOCALE = 'es-MX';

export interface FormatoMoneda {
  /** Importe con su moneda. `null`/NaN → guion. Sin moneda conocida: solo el número. */
  money: (n: number | null | undefined) => string;
  /** Igual que `money` pero sin decimales (tarjetas, totales de un vistazo). */
  moneyCorto: (n: number | null | undefined) => string;
  /** Número suelto (cantidades, conteos). No lleva moneda nunca. */
  num: (n: number | null | undefined, decimales?: number) => string;
  /**
   * Monto partido en número y código de moneda, para los TOTALES GRANDES de las
   * tarjetas. Existe porque el código ("CLP") ocupa tres caracteres más que un
   * signo y, en tipografía de titular, un total de miles de millones se cortaba a
   * la mitad — y un total cortado miente peor que un símbolo. Acá el número va
   * grande y el código chico al lado, así entra siempre y sigue diciendo la moneda.
   */
  partes: (n: number | null | undefined, decimales?: number) => { numero: string; codigo: string | null };
  /** La moneda vigente (ISO 4217) o `null` si no se sabe. */
  moneda: string | null;
}

/** ISO 4217 = 3 letras. Lo que no lo sea se trata como "no se sabe". */
function isoValida(moneda: string | null | undefined): string | null {
  if (typeof moneda !== 'string') return null;
  const s = moneda.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(s) ? s : null;
}

function crearIntl(moneda: string | null, decimales: number): Intl.NumberFormat {
  if (moneda) {
    try {
      return new Intl.NumberFormat(LOCALE, {
        style: 'currency',
        currency: moneda,
        minimumFractionDigits: decimales,
        maximumFractionDigits: decimales,
      });
    } catch {
      // Código con forma de ISO que Intl no conoce: se cae al número pelado y la
      // moneda se agrega como texto (mejor que reventar la pantalla).
    }
  }
  return new Intl.NumberFormat(LOCALE, { minimumFractionDigits: decimales, maximumFractionDigits: decimales });
}

/**
 * Formateadores para una obra. Se crea una vez por pantalla (los `Intl` son
 * caros de construir) a partir de la moneda de la obra.
 */
export function formatoDeMoneda(moneda: string | null | undefined): FormatoMoneda {
  const iso = isoValida(moneda);
  const conDecimales = crearIntl(iso, 2);
  const sinDecimales = crearIntl(iso, 0);
  const fmt = (f: Intl.NumberFormat) => (n: number | null | undefined): string => {
    if (n == null || Number.isNaN(Number(n))) return '—';
    return f.format(Number(n));
  };
  return {
    money: fmt(conDecimales),
    moneyCorto: fmt(sinDecimales),
    num: (n, decimales = 2) => {
      if (n == null || Number.isNaN(Number(n))) return '—';
      return Number(n).toLocaleString(LOCALE, { maximumFractionDigits: decimales });
    },
    partes: (n, decimales = 0) => ({
      numero: n == null || Number.isNaN(Number(n))
        ? '—'
        : Number(n).toLocaleString(LOCALE, { minimumFractionDigits: decimales, maximumFractionDigits: decimales }),
      codigo: iso,
    }),
    moneda: iso,
  };
}

/**
 * Para el caso en que ni siquiera hay obra: mismo contrato, sin moneda. Sirve como
 * default de props para que ningún componente tenga que repetir el `?? '$'`.
 */
export const SIN_MONEDA = formatoDeMoneda(null);

/**
 * La moneda de la obra que se está mirando, por contexto de React. Existe para las
 * pantallas que arman su tabla en varios componentes chicos (el detalle de los
 * análisis, la hoja de impresión): sin esto habría que pasar el formateador por
 * cinco niveles de props, y el que se olvidara volvería a escribir pesos mexicanos.
 *
 * El default es SIN moneda a propósito: un componente que se monte fuera del
 * proveedor muestra números sin símbolo, no números en la moneda equivocada.
 *
 * Uso: `<MonedaObraCtx.Provider value={formatoDeMoneda(quote?.moneda)}>` en la
 * pantalla, y `const { money } = useMonedaObra()` en cada hijo.
 */
export const MonedaObraCtx = createContext<FormatoMoneda>(SIN_MONEDA);

export function useMonedaObra(): FormatoMoneda {
  return useContext(MonedaObraCtx);
}
